/**
 * Biblioteca de componentes.
 *
 * Todos usam as classes semânticas do tema (`bg-surface`, `text-ink`) em vez
 * de cor literal. É o que faz o modo escuro trocar tudo de uma vez: as
 * variáveis CSS mudam e nenhum componente precisa saber disso.
 */

import {
  POSITION_LABELS_PT,
  POSITION_ORDER,
  POSITION_SHORT_PT,
  type PlayerPosition,
} from '@nossorachao/types';
import type { LucideIcon } from 'lucide-react';
import { AlertCircle, Check, Hand, Loader2, Star } from 'lucide-react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

export function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Maiúscula só na primeira letra.
 *
 * A classe `capitalize` do CSS capitaliza cada palavra, o que transforma
 * "segunda-feira, 17 de agosto" em "Segunda-Feira, 17 De Agosto" — errado em
 * português. O Intl devolve tudo minúsculo, então o ajuste é aqui.
 */
export function capitalizeFirst(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// =============================================================================
// Botão
// =============================================================================

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-hover',
  success: 'bg-ok text-on-brand hover:opacity-90',
  danger: 'bg-bad text-white hover:opacity-90',
  secondary: 'bg-sunken text-ink border border-line hover:border-line-strong',
  ghost: 'text-brand hover:bg-brand-soft',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px] gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-[15px] gap-2',
};

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  loading = false,
  full = false,
  className,
  disabled,
  ...rest
}: {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  loading?: boolean;
  full?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const inactive = disabled || loading;

  return (
    <button
      type="button"
      disabled={inactive}
      aria-busy={loading}
      className={cx(
        'inline-flex items-center justify-center rounded-lg font-semibold',
        'transition-colors duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        full && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : Icon ? (
        <Icon className="h-4 w-4" aria-hidden />
      ) : null}
      {children}
    </button>
  );
}

// =============================================================================
// Card
// =============================================================================

export function Card({
  children,
  className,
  accent,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  /** Faixa colorida à esquerda — cor de time, estado financeiro. */
  accent?: string;
  onClick?: () => void;
}) {
  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cx(
        'card relative overflow-hidden p-5',
        onClick && 'w-full cursor-pointer text-left transition-colors hover:border-line-strong',
        className,
      )}
    >
      {accent ? (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-1"
          style={{ backgroundColor: accent }}
        />
      ) : null}
      {children}
    </Wrapper>
  );
}

// =============================================================================
// Badge
// =============================================================================

type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'info' | 'brand';

const BADGE_TONES: Record<Tone, string> = {
  neutral: 'bg-sunken text-ink-2',
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  bad: 'bg-bad-soft text-bad',
  info: 'bg-info-soft text-info',
  brand: 'bg-brand-soft text-brand',
};

export function Badge({
  children,
  tone = 'neutral',
  icon: Icon,
}: {
  children: ReactNode;
  tone?: Tone;
  icon?: LucideIcon;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-pill px-2 py-0.5',
        'text-[10px] font-bold uppercase tracking-wide',
        BADGE_TONES[tone],
      )}
    >
      {Icon ? <Icon className="h-3 w-3" aria-hidden /> : null}
      {children}
    </span>
  );
}

// =============================================================================
// Avatar
// =============================================================================

export function Avatar({
  name,
  src,
  size = 36,
  badge: BadgeIcon,
}: {
  name: string;
  src?: string | null;
  size?: number;
  badge?: LucideIcon;
}) {
  const initials =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '?';

  // Cor derivada do nome: estável entre sessões e distinta entre jogadores,
  // sem precisar guardar nada.
  const hue = [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) % 360;

  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      {src ? (
        <img
          src={src}
          alt=""
          className="h-full w-full rounded-full object-cover"
          loading="lazy"
        />
      ) : (
        <span
          className="flex h-full w-full items-center justify-center rounded-full font-bold"
          style={{
            backgroundColor: `hsl(${hue} 45% 88%)`,
            color: `hsl(${hue} 55% 28%)`,
            fontSize: size * 0.38,
          }}
        >
          {initials}
        </span>
      )}

      {BadgeIcon ? (
        <span
          className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full bg-brand ring-2 ring-surface"
          style={{ width: size * 0.42, height: size * 0.42 }}
        >
          <BadgeIcon className="text-on-brand" style={{ width: size * 0.22, height: size * 0.22 }} />
        </span>
      ) : null}
    </span>
  );
}

// =============================================================================
// Nota em estrelas
// =============================================================================

export function StarRating({
  value,
  size = 14,
  showValue = true,
  interactive = false,
  onChange,
}: {
  value: number;
  size?: number;
  showValue?: boolean;
  interactive?: boolean;
  onChange?: (value: number) => void;
}) {
  const rounded = Math.round(value * 2) / 2;

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => {
          const filled = rounded >= star;
          const half = !filled && rounded >= star - 0.5;

          const icon = (
            <span className="relative inline-block" style={{ width: size, height: size }}>
              <Star
                className="absolute inset-0 text-ink-3"
                style={{ width: size, height: size }}
                strokeWidth={1.5}
              />
              {filled || half ? (
                <span
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: half ? size / 2 : size }}
                >
                  <Star
                    className="text-[#F79009]"
                    style={{ width: size, height: size }}
                    fill="currentColor"
                    strokeWidth={1.5}
                  />
                </span>
              ) : null}
            </span>
          );

          if (!interactive) return <span key={star}>{icon}</span>;

          return (
            <button
              key={star}
              type="button"
              aria-label={`${star} estrela${star > 1 ? 's' : ''}`}
              // Tocar de novo na mesma estrela alterna para meia — dá acesso à
              // escala de 0,5 sem um segundo controle na tela.
              onClick={() => onChange?.(rounded === star ? star - 0.5 : star)}
              className="transition-transform hover:scale-110"
            >
              {icon}
            </button>
          );
        })}
      </span>

      {showValue ? (
        <span className="tabular text-[13px] font-semibold text-ink-2">
          {value.toFixed(1).replace('.', ',')}
        </span>
      ) : null}
    </span>
  );
}

// =============================================================================
// Formulário
// =============================================================================

export function Field({
  label,
  error,
  hint,
  children,
  htmlFor,
}: {
  label?: string;
  error?: string;
  hint?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div>
      {label ? (
        <label className="label" htmlFor={htmlFor}>
          {label}
        </label>
      ) : null}

      {children}

      {/* Erro tem prioridade: nunca mostra os dois e confunde. */}
      {error ? (
        <p className="mt-1.5 flex items-center gap-1 text-[13px] text-bad">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1.5 text-[13px] text-ink-3">{hint}</p>
      ) : null}
    </div>
  );
}

export function Input({
  invalid,
  className,
  ...rest
}: { invalid?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      aria-invalid={invalid}
      className={cx('input', invalid && 'border-bad focus:border-bad focus:ring-bad/25', className)}
      {...rest}
    />
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <span
        className={cx(
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
          checked ? 'border-brand bg-brand' : 'border-line-strong',
        )}
      >
        {checked ? <Check className="h-3.5 w-3.5 text-on-brand" strokeWidth={3} /> : null}
      </span>

      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="sr-only"
      />

      <span className="text-[13px] leading-relaxed text-ink-2">{label}</span>
    </label>
  );
}

// =============================================================================
// Estados
// =============================================================================

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-shimmer rounded bg-sunken', className)} aria-hidden />;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sunken">
        <Icon className="h-7 w-7 text-ink-3" aria-hidden />
      </span>

      <h3 className="text-lg font-bold">{title}</h3>
      {description ? <p className="max-w-sm text-sm text-ink-2">{description}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Card accent="rgb(217 45 32)">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-bad" aria-hidden />
        <div className="flex-1">
          <p className="font-semibold">Não foi possível carregar</p>
          <p className="mt-0.5 text-sm text-ink-2">{message}</p>
        </div>
        {onRetry ? (
          <Button size="sm" variant="secondary" onClick={onRetry}>
            Tentar de novo
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

// =============================================================================
// Dados
// =============================================================================

export function StatTile({
  label,
  value,
  hint,
  tone = 'default',
  icon: Icon,
  onClick,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'ok' | 'warn' | 'bad';
  icon?: LucideIcon;
  onClick?: () => void;
}) {
  const toneClass = {
    default: 'text-ink',
    ok: 'text-ok',
    warn: 'text-warn',
    bad: 'text-bad',
  }[tone];

  const content = (
    <>
      <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-ink-3">
        {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden /> : null}
        {label}
      </p>
      <p className={cx('tabular mt-1 text-2xl font-extrabold', toneClass)}>{value}</p>
      {hint ? <p className="mt-0.5 text-[13px] text-ink-3">{hint}</p> : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="card p-5 text-left transition-colors hover:border-brand"
      >
        {content}
      </button>
    );
  }

  return <div className="card p-5">{content}</div>;
}

export function ProgressBar({
  value,
  max,
  tone = 'brand',
}: {
  value: number;
  max: number;
  tone?: 'brand' | 'ok' | 'warn' | 'bad';
}) {
  const percent = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const bar = { brand: 'bg-brand', ok: 'bg-ok', warn: 'bg-warn', bad: 'bg-bad' }[tone];

  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-pill bg-sunken"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <div className={cx('h-full transition-all duration-300', bar)} style={{ width: `${percent}%` }} />
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ key: T; label: string; count?: number }>;
  active: T;
  onChange: (key: T) => void;
}) {
  return (
    // No celular as abas rolam de lado em vez de quebrar em duas linhas; a
    // margem negativa deixa a rolagem chegar até a borda da tela.
    <div
      className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden"
      role="tablist"
    >
      {tabs.map((tab) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.key)}
            className={cx(
              'shrink-0 whitespace-nowrap rounded-pill border px-4 py-1.5 text-[13px] font-semibold transition-colors',
              selected
                ? 'border-brand bg-brand text-on-brand'
                : 'border-line bg-surface text-ink-2 hover:border-line-strong',
            )}
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className={cx('ml-1.5 tabular', selected ? 'opacity-80' : 'text-ink-3')}>
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <h2 className="text-lg font-bold">{children}</h2>
      {action}
    </div>
  );
}

// =============================================================================
// Posição em campo
// =============================================================================

/**
 * Etiqueta da posição.
 *
 * Cor por setor, não uma por posição: zagueiro e lateral compartilham o tom
 * defensivo porque é assim que se lê um time de relance — quantos atrás,
 * quantos na frente. Cinco cores distintas viram enfeite e param de informar.
 */
const POSITION_TONE: Record<PlayerPosition, Tone> = {
  GOALKEEPER: 'warn',
  DEFENDER: 'info',
  FULLBACK: 'info',
  MIDFIELDER: 'brand',
  FORWARD: 'bad',
};

export function PositionBadge({
  position,
  canPlayGoalkeeper = false,
  short = false,
}: {
  position: PlayerPosition;
  /** Mostra a mãozinha de "aceita ir ao gol" em quem não é goleiro de ofício. */
  canPlayGoalkeeper?: boolean;
  short?: boolean;
}) {
  const alsoKeeper = canPlayGoalkeeper && position !== 'GOALKEEPER';

  return (
    <span className="inline-flex items-center gap-1">
      <Badge tone={POSITION_TONE[position]}>
        {short ? POSITION_SHORT_PT[position] : POSITION_LABELS_PT[position]}
      </Badge>
      {alsoKeeper ? (
        <span
          title="Também aceita jogar no gol"
          className="inline-flex items-center text-ink-3"
          aria-label="Também aceita jogar no gol"
        >
          <Hand className="h-3 w-3" aria-hidden />
        </span>
      ) : null}
    </span>
  );
}

/**
 * Seletor de posição.
 *
 * Botões em vez de `<select>`: são cinco opções fixas, e ver todas de uma vez
 * (na ordem do gol para o ataque) é mais rápido que abrir uma lista.
 */
export function PositionPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: PlayerPosition;
  onChange: (position: PlayerPosition) => void;
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label="Posição" className="flex flex-wrap gap-2">
      {POSITION_ORDER.map((position) => {
        const selected = position === value;

        return (
          <button
            key={position}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(position)}
            className={cx(
              'rounded-pill border px-3.5 py-1.5 text-[13px] font-semibold transition-colors',
              'disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'border-brand bg-brand text-on-brand'
                : 'border-line bg-surface text-ink-2 hover:border-line-strong',
            )}
          >
            {POSITION_LABELS_PT[position]}
          </button>
        );
      })}
    </div>
  );
}
